import React, { useState } from 'react';
import styled from 'styled-components';

import { useQueryWalletDelegatedKeys } from '#/api/queries/delegatedKeysQueries';
import Button from '#/components/basic/Button/Button';
import MaterialSymbol from '#/components/basic/icons/MaterialSymbol';
import Link from '#/components/basic/Link/Link';
import LinkClickable from '#/components/basic/Link/LinkClickable';
import BasicTableStackable from '#/components/basic/table/BasicTableStackable';
import TextColored from '#/components/basic/text/TextColored';
import MetaTags from '#/components/layout/header/MetaTags';
import { KATANA_PERPS, urls } from '#/config/constants';
import { useIsMobile } from '#/hooks/useIsMobile';
import { formatDateTime } from '#/lib/date';
import { formatUserAgent } from '#/lib/userAgent';
import { checkDelegatedExpiring } from '#/reduxState/delegatedKey/authorizationUtils';
import { useSelector } from '#/reduxState/store';

import WalletDashboardContent from '../dashboard/WalletDashboardContent';

import CreateDelegatedKeyModal from './CreateDelegatedKeyModal';
import { useInvalidateAllModal } from './hooks/useInvalidateModal';
import { useReauthorizeModal } from './hooks/useReauthorizeModal';
import { useRevokeModal } from './hooks/useRevokeModal';
import UnlockChoiceButtons from './UnlockChoiceButtons';

const AgentColumn = styled.div`
  overflow: hidden;
  max-width: 500px;
  white-space: pre-wrap;
`;

const AuthActions = styled.div`
  display: flex;
  gap: 15px;
`;

const ActionsColumn = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 15px;
  padding-right: 14px;
`;

const Notice = styled.div`
  color: var(--gray4b);
  text-align: center;
  font-size: var(--small);
`;

function WalletAuthorizationsPage() {
  const { data = [] } = useQueryWalletDelegatedKeys();

  const isMobile = useIsMobile();
  const wallet = useSelector(state => state.wallet);
  const { walletAddress } = wallet;

  const currentDelegatedKey = useSelector(
    state => state.delegatedKey.authorization
  );

  const [createModal, setCreateModal] = useState(false);

  const [revokeModalPlaceholder, displayRevokeModal] =
    useRevokeModal(currentDelegatedKey);

  const [reauthorizeModalPlaceholder, displayReauthorizeModal] =
    useReauthorizeModal(currentDelegatedKey);

  const [invalidateAllPlaceholder, displayInvalidateAll] =
    useInvalidateAllModal(currentDelegatedKey);
  return (
    <>
      {invalidateAllPlaceholder}
      {revokeModalPlaceholder}
      {reauthorizeModalPlaceholder}

      {createModal && (
        <CreateDelegatedKeyModal onClose={() => setCreateModal(false)} />
      )}

      <MetaTags title="Session Keys" />

      <WalletDashboardContent
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            Session Keys
            <Link href={urls.delegatedKeysDocs} style={{ paddingTop: 4 }}>
              <MaterialSymbol icon="info" fontSize="small" />
            </Link>
          </div>
        }
        headerContentRight={
          wallet.walletType === 'qrSynced' ? undefined : (
            <AuthActions>
              <Button
                inline
                onClick={() => displayInvalidateAll()}
                bordered
                disabled={!walletAddress}
                color="red"
              >
                Invalidate All
              </Button>
              <Button
                inline
                disabled={!walletAddress}
                onClick={() => setCreateModal(true)}
              >
                Create New Key
              </Button>
            </AuthActions>
          )
        }
      >
        <BasicTableStackable
          notFixedLayout
          items={data}
          getItemKey={item =>
            `${item.authorizedAt}_${item.name ?? item.delegatedKey}`
          }
          errorText={
            !walletAddress
              ? 'Connect wallet to see your authorizations'
              : undefined
          }
          stripes
          rowCustomizer={item => {
            const { state } = checkDelegatedExpiring(item.authorizedAt, 0);
            return {
              background:
                state === 'warnFirst'
                  ? 'warn'
                  : state === 'warnLast' || state === 'expired'
                  ? 'error'
                  : undefined,
            };
          }}
          columns={item => {
            const createdAt = item?.authorizedAt || 0;
            const { state, remainingTime } = checkDelegatedExpiring(
              createdAt,
              0
            );
            const isCurrentSessionKey =
              item?.delegatedKey.toLowerCase() ===
              currentDelegatedKey.walletAddress.toLowerCase();
            return [
              {
                name: 'Authorized',
                padding: '0 0 0 20px',
                // width: '200px',
                render: (
                  <span style={{ paddingLeft: 20 }}>
                    {formatDateTime(createdAt)}
                  </span>
                ),
              },
              {
                name: 'Expires',
                padding: '0 0 0 20px',
                // width: '200px',
                fontFamily: 'standard',
                stackWithPrevious: isMobile,
                render: (
                  <span
                    style={{
                      paddingLeft: 20,
                      color:
                        state === 'warnLast' || state === 'expired'
                          ? 'var(--red3)'
                          : state === 'warnFirst'
                          ? 'var(--warnContent)'
                          : undefined,
                    }}
                  >
                    {state === 'expired' ? 'expired' : `in ${remainingTime}`}
                  </span>
                ),
              },
              {
                name: 'Client',
                fontFamily: 'standard',
                render: item => {
                  return (
                    <AgentColumn>
                      {item.name === `${KATANA_PERPS} Connect`
                        ? `${formatUserAgent(
                            item.userAgent
                          )} (${KATANA_PERPS} Connect)`
                        : item.name ?? formatUserAgent(item.userAgent)}{' '}
                      {isCurrentSessionKey ? (
                        <TextColored color="green">
                          {' '}
                          <b>Active Session</b>
                        </TextColored>
                      ) : null}
                    </AgentColumn>
                  );
                },
              },
              {
                name: 'Actions',
                align: 'right',
                fontFamily: 'standard',
                padding: '0 20px 0 0',
                render:
                  wallet.walletType === 'qrSynced'
                    ? false
                    : item => (
                        <ActionsColumn>
                          <LinkClickable
                            onClick={() =>
                              displayReauthorizeModal({
                                delegatedKey: item.delegatedKey,
                                delegatedName: item.name,
                              })
                            }
                          >
                            Reauthorize
                          </LinkClickable>
                          <LinkClickable
                            onClick={() =>
                              displayRevokeModal({
                                delegatedKey: item.delegatedKey,
                              })
                            }
                            color="red"
                          >
                            Remove
                          </LinkClickable>
                        </ActionsColumn>
                      ),
              },
            ];
          }}
        />
        {!walletAddress && <UnlockChoiceButtons />}
        {data.length > 0 && (
          <Notice>
            Do not share your Session Keys with any untrusted third party.
          </Notice>
        )}
      </WalletDashboardContent>
    </>
  );
}

export default WalletAuthorizationsPage;
